import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../redux";
import { DataTable } from "../../components/DataTable";
import { PageLayout } from "../../components/PageLayout";
import { Evaluator, evaluatorActions } from "../../redux/reducers/evaluatorSlice";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";

export const EvaluatorsPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { organization } = useEffectiveOrganization();

  const evaluatorEntities = useAppSelector((state) => state.evaluator.entities);
  const evaluatorsIsLoading = useAppSelector((state) => state.evaluator.isLoading);

  const evaluators = useMemo(() => {
    if (evaluatorEntities && organization) {
      return Object.values(evaluatorEntities)
        .filter((evaluator) => evaluator.organizationId === organization.id)
        .map((evaluator) => ({
          ...evaluator,
          name: evaluator.title,
          description: `${evaluator.evalType} · ${evaluator.targetFormat}`,
        }));
    }
    return [];
  }, [evaluatorEntities, organization]);

  useEffect(() => {
    if (organization) {
      dispatch(evaluatorActions.get({}));
    }
  }, [dispatch, organization]);

  const onClickItem = (id: string) => {
    navigate(`/evaluators/${id}/details`);
  };

  const onClickCreate = () => {
    navigate("/evaluators/new");
  };

  const filterEvaluators = (searchQuery: string, item: Record<string, unknown>) => {
    const evaluator = item as Evaluator & { name: string; description: string };
    const name = evaluator.name?.toLowerCase() || "";
    const description = evaluator.description?.toLowerCase() || "";

    return name.includes(searchQuery.toLowerCase()) || description.includes(searchQuery.toLowerCase());
  };

  const sortEvaluators = (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const evalA = a as Evaluator;
    const evalB = b as Evaluator;

    const aTime = evalA.updatedAt || "";
    const bTime = evalB.updatedAt || "";
    return new Date(bTime).getTime() - new Date(aTime).getTime();
  };

  return (
    <PageLayout
      title="Evaluators"
      description="Create evaluators to score or extract data from your agent sessions."
    >
      <DataTable
        key={evaluators.length}
        entityName="evaluator"
        emptyMessage={`Create your first evaluator to get started.\nEvaluators can score or extract data from agent sessions.`}
        filter={filterEvaluators}
        sort={sortEvaluators}
        isLoading={evaluatorsIsLoading}
        onClickCreate={onClickCreate}
        items={evaluators}
        onRowClick={onClickItem}
        showIcons={true}
        iconName="checklist"
        timestampLabel="Last Updated"
        createStyle="row"
        createRowLabel="Create evaluator"
        hideEmptyMessage
      />
    </PageLayout>
  );
};
